import React, { useMemo, useState } from 'react';
import ThumbnailStrip from '../Shared/ThumbnailStrip';
import { FaCalendarAlt, FaImage, FaExclamationTriangle, FaFlag, FaSort, FaSortUp, FaSortDown, FaExclamationCircle } from 'react-icons/fa';

function Pill({ color = '#ef4444', bg = '#fee2e2', children }) {
  return (
    <span style={{ background: bg, color, borderRadius: 999, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>
      {children}
    </span>
  );
}

function getStatusPill(status) {
  const baseStyle = {
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '600',
    textAlign: 'center',
    display: 'inline-block',
    minWidth: '70px'
  };

  switch (status) {
    case 'Ongoing':
      return {
        ...baseStyle,
        backgroundColor: '#fef2f2',
        color: '#dc2626'
      };
    case 'Resolved':
      return {
        ...baseStyle,
        backgroundColor: '#f0fdf4',
        color: '#16a34a'
      };
    default:
      return {
        ...baseStyle,
        backgroundColor: '#f3f4f6',
        color: '#6b7280'
      };
  }
}

// Helper function to extract date string from date object or string
function getDateString(dateValue) {
  if (!dateValue) return '';
  if (typeof dateValue === 'string') return dateValue;
  if (typeof dateValue === 'object' && dateValue.iso) return dateValue.iso;
  return '';
}

// Helper function to format date for display
function formatDate(dateValue) {
  const dateStr = getDateString(dateValue);
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) {
    return dateStr;
  }
}

export default function IssuesTable({ issues, onOpenInspectionDetails, onStatusToggle }) {
  const [sortColumn, setSortColumn] = useState('title');
  const [sortDirection, setSortDirection] = useState('asc');
  const [rowsPerPage, setRowsPerPage] = useState(3);
  const [currentPage, setCurrentPage] = useState(1);

  const normalized = Array.isArray(issues) ? issues : [];

  const sorted = useMemo(() => {
    const data = [...normalized];
    data.sort((a, b) => {
      let va = a[sortColumn];
      let vb = b[sortColumn];
      
      if (sortColumn === 'dateFound') {
        const dateStrA = getDateString(va);
        const dateStrB = getDateString(vb);
        va = dateStrA ? new Date(dateStrA).getTime() : 0;
        vb = dateStrB ? new Date(dateStrB).getTime() : 0;
      } else if (sortColumn === 'priority') {
        // Define priority order: High = 1, Medium = 2, Low = 3
        const priorityOrder = { 'High': 1, 'Medium': 2, 'Low': 3 };
        va = priorityOrder[va] || 999;
        vb = priorityOrder[vb] || 999;
      } else {
        va = (va || '').toString().toLowerCase();
        vb = (vb || '').toString().toLowerCase();
      }
      
      if (va < vb) return sortDirection === 'asc' ? -1 : 1;
      if (va > vb) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [normalized, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sorted.length / rowsPerPage) || 1;
  const start = (currentPage - 1) * rowsPerPage;
  const pageData = sorted.slice(start, start + rowsPerPage);

  function handleSort(column) {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  }

  function getSortIcon(column) {
    if (sortColumn !== column) {
      return <FaSort style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '4px' }} />;
    }
    return sortDirection === 'asc' 
      ? <FaSortUp style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }} />
      : <FaSortDown style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }} />;
  }


  return (
    <div className="bg-white rounded-lg p-4" style={{ marginTop: 16, borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <div style={{ fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <div style={{ 
          width: '20px', 
          height: '20px', 
          backgroundColor: '#dbeafe', 
          borderRadius: '4px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          fontSize: '12px',
          color: '#3b82f6'
        }}>
          <FaExclamationTriangle />
        </div>
        Issues & Recommendations
      </div>
      <div className="manager-table-scroll" role="region" aria-label="Equipment issues, scroll horizontally for all columns" tabIndex={0} style={{ overflowX: 'auto' }}>
        <table className="table mechanical-history-table" style={{ marginBottom: 0 }}>
          <thead>
            <tr>
              <th style={{ cursor: 'pointer', fontWeight: '600', color: '#374151' }} tabIndex={0} aria-sort={sortColumn === 'title' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleSort('title'); } }} onClick={() => handleSort('title')}>
                Issue{getSortIcon('title')}
              </th>
              <th style={{ cursor: 'pointer', fontWeight: '600', color: '#374151' }} tabIndex={0} aria-sort={sortColumn === 'dateFound' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleSort('dateFound'); } }} onClick={() => handleSort('dateFound')}>
                Date Found{getSortIcon('dateFound')}
              </th>
              <th style={{ fontWeight: '600', color: '#374151' }}>Attachments</th>
              <th style={{ cursor: 'pointer', fontWeight: '600', color: '#374151' }} tabIndex={0} aria-sort={sortColumn === 'priority' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleSort('priority'); } }} onClick={() => handleSort('priority')}>
                Priority{getSortIcon('priority')}
              </th>
              <th style={{ cursor: 'pointer', fontWeight: '600', color: '#374151' }} tabIndex={0} aria-sort={sortColumn === 'status' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleSort('status'); } }} onClick={() => handleSort('status')}>
                Status{getSortIcon('status')}
              </th>
              <th style={{ fontWeight: '600', color: '#374151' }}>Report</th>
            </tr>
          </thead>
          <tbody>
            {normalized.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af', fontStyle: 'italic' }}>
                  No issues found
                </td>
              </tr>
            ) : (
              pageData.map((row) => (
              <tr
                key={row.id}
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => {
                  const tds = e.currentTarget.querySelectorAll('td');
                  tds.forEach((td) => { td.style.backgroundColor = '#f3f4f6'; });
                }}
                onMouseLeave={(e) => {
                  const tds = e.currentTarget.querySelectorAll('td');
                  tds.forEach((td) => { td.style.backgroundColor = ''; });
                }}
                onClick={() => {
                  if (typeof onOpenInspectionDetails === 'function') onOpenInspectionDetails(row, 'issue');
                }}
              >
                <td style={{ color: '#374151' }}>{row.title}</td>
                <td style={{ color: '#374151' }}>{formatDate(row.dateFound)}</td>
                <td>
                  {row.photos?.length ? (
                    <ThumbnailStrip images={row.photos} label="issue photo" onOpen={(index) => onOpenInspectionDetails?.({ ...row, initialImageIndex: index }, 'issue')} />
                  ) : null}
                </td>
                <td>
                  {row.priority === 'High' ? (
                    <Pill color="#b91c1c" bg="#fee2e2">High</Pill>
                  ) : row.priority === 'Medium' ? (
                    <Pill color="#ea580c" bg="#fed7aa">Medium</Pill>
                  ) : (
                    <Pill color="#ca8a04" bg="#fef3c7">Low</Pill>
                  )}
                </td>
                <td style={{ color: '#374151' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (typeof onStatusToggle === 'function') {
                        const nextStatus = row.status === 'Resolved' ? 'Ongoing' : 'Resolved';
                        onStatusToggle(row, nextStatus);
                      }
                    }}
                    style={{
                      ...getStatusPill(row.status),
                      border: getStatusPill(row.status).border || '1px solid transparent',
                      backgroundColor: getStatusPill(row.status).backgroundColor,
                      cursor: 'pointer'
                    }}
                  >
                    {row.status}
                  </button>
                </td>
                <td>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (typeof onOpenInspectionDetails === 'function') onOpenInspectionDetails(row, 'issue');
                    }}
                    style={{
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '0.25rem 0.75rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <FaExclamationCircle style={{ fontSize: '0.75rem', color: '#9ca3af' }} />
                    View Issue Report
                  </button>
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer controls - only show when there are issues */}
      {normalized.length > 0 && (
        <div className="manager-table-pagination" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <div>
            <label style={{ marginRight: 8, color: '#6b7280' }}>Rows per page</label>
            <select
              aria-label="Rows per page"
              value={rowsPerPage}
              onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              className="form-select"
              style={{ width: 100, display: 'inline-block' }}
            >
              <option value={3}>3</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
            </select>
          </div>
          <div style={{ color: '#6b7280' }}>
            <button className="btn btn-light btn-sm" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>Prev</button>
            <span style={{ margin: '0 8px' }}>{currentPage} / {totalPages}</span>
            <button className="btn btn-light btn-sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>Next</button>
          </div>
        </div>
      )}
    </div>
  );
}


